<script setup lang="ts">
definePageMeta({ prerender: true })

const { data: games } = await useAsyncData('games', () => queryCollection('games').first())

useSeoMeta({
  description: 'Every game in the Signal Fire library, live and on the way.',
  ogDescription: 'Every game in the Signal Fire library, live and on the way.',
  title: 'Games',
})

// Live first, then coming-soon, each group sorted by order.
const allGames = computed(() => {
  const list = games.value?.games ?? []
  const byOrder = (a: typeof list[number], b: typeof list[number]) => (a.order ?? 99) - (b.order ?? 99)
  const live = list.filter(g => g.status === 'available').sort(byOrder)
  const soon = list.filter(g => g.status === 'coming-soon').sort(byOrder)
  return [ ...live, ...soon ]
})

const liveCount = computed(() => allGames.value.filter(g => g.status === 'available').length)
const soonCount = computed(() => allGames.value.filter(g => g.status === 'coming-soon').length)

const countLabel = computed(() => {
  const fmt = games.value?.library.countFormat ?? '{live} LIVE / {soon} IN THE WORKS'
  return fmt
    .replace('{live}', String(liveCount.value))
    .replace('{soon}', String(soonCount.value))
})

const isEmpty = computed(() => allGames.value.length === 0)

// Tilt cadence for the grid, keyed off slug-stable index so tiles
// don't all lean the same direction in a row.
const TILE_TILTS = [ 1, 3, 4, 2 ] as const
const tiltFor = (i: number) => TILE_TILTS[i % TILE_TILTS.length]!
</script>

<template>
  <div v-if="games">
    <!-- ===== Hero ===== -->
    <SfgPaper :padded="false">
      <div class="mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
        <SfgEyebrow variant="outline">
          {{ games.library.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="1" class="mt-5 max-w-3xl">
          {{ games.library.heading }}
        </SfgHeading>
        <div class="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 max-w-3xl">
          <p class="font-body text-body max-w-xl">
            {{ games.library.lead }}
          </p>
          <span class="font-display uppercase text-[11px] tracking-[0.18em] text-brand-ink/60 whitespace-nowrap">
            {{ countLabel }}
          </span>
        </div>
      </div>
    </SfgPaper>

    <!-- ===== Empty state ===== -->
    <SfgPaper v-if="isEmpty" :padded="false">
      <div class="mx-auto max-w-2xl px-6 pb-24 text-center flex flex-col items-center gap-6">
        <SfgFlame
          :size="120"
          bg-color="red"
          flame-color="yellow"
          :tilt="2"
        />
        <p class="font-body text-body">
          {{ games.library.emptyMessage }}
        </p>
      </div>
    </SfgPaper>

    <!-- ===== Unified grid: live tiles linked, coming-soon as non-interactive ===== -->
    <!-- TODO: filters, add when library > 5 games -->
    <SfgPaper v-else :padded="false">
      <div class="mx-auto max-w-7xl px-6 pb-24 sm:px-10">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <template v-for="(game, i) in allGames" :key="game.slug">
            <NuxtLink
              v-if="game.status === 'available' && game.route"
              :to="game.route"
              class="block focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-ink rounded-card"
            >
              <SfgGameTile
                :title="game.title"
                :format="game.format"
                :type="game.type"
                :duration="game.duration"
                :player-note="game.playerNote"
                status="live"
                :sticker-top="game.stickerTop"
                :sticker-bottom="game.stickerBottom"
                :sticker-tilt="tiltFor(i)"
                :art-fill="game.artFill"
              />
            </NuxtLink>
            <SfgGameTile
              v-else
              :title="game.title"
              :format="game.format"
              :type="game.type"
              :duration="game.duration"
              :player-note="game.playerNote"
              status="soon"
              :sticker-top="game.stickerTop"
              :sticker-bottom="game.stickerBottom"
              :sticker-tilt="tiltFor(i)"
              :art-fill="game.artFill"
            />
          </template>
        </div>
      </div>
    </SfgPaper>
  </div>
</template>
